import { useContactSellerMutation } from '@kijiji/generated/graphql-types'
import { type FieldProps, Field, FieldArray, Formik } from 'formik'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'
import { useTheme } from 'styled-components'

import { refreshSession } from '@/features/auth/utils/refreshSession'
import { AvailableDatesField } from '@/features/listing/components/request-viewing/RequestViewForm/AvailableDatesField'
import { MAX_AVAILABLE_DATES } from '@/features/listing/components/request-viewing/RequestViewing'
import {
  Form,
  FormBodyFlexContainer,
  FormFooter,
} from '@/features/listing/components/request-viewing/styled'
import { buildRequestViewingMessage } from '@/features/listing/utils/request-viewing/buildRequestViewingMessage'
import { generateRequestViewingAttemptTrackingLabel } from '@/features/listing/utils/request-viewing/generateRequestViewingAttemptTrackingLabel'
import { validateDuplicateDates } from '@/features/listing/utils/request-viewing/validateDuplicateDates'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text/BodyText'
import { Button } from '@/ui/atoms/button'
import { Spacing } from '@/ui/atoms/spacing'
import { TextField } from '@/ui/molecules/text-field/TextField'
import { validationFnPerField } from '@/utils/validation'

type RequestViewingFormProps = {
  listingId: string
  onSuccess: () => void
  onError: () => void
  source: string
}

export type AvailableDate = {
  date: string
  timeFrom: string
  timeTo: string
}

export type FormValues = {
  fullName: string
  availableDates: AvailableDate[]
}

export const RequestViewingForm = ({
  listingId,
  onError,
  onSuccess,
  source,
}: RequestViewingFormProps) => {
  const { t } = useTranslation(['common', 'vip'])
  const [contactSeller, { loading: isSubmittingMessage }] = useContactSellerMutation()
  const { routeLocale } = useLocale()
  const { colors, spacing } = useTheme()
  const { status } = useSession()

  const initialValues: FormValues = {
    fullName: '',
    availableDates: [
      {
        date: '',
        timeFrom: '',
        timeTo: '',
      },
    ],
  }

  const handleSubmit = async (data: FormValues) => {
    const formattedMessage = buildRequestViewingMessage(
      data.fullName,
      data.availableDates,
      routeLocale
    )

    try {
      await refreshSession(status)
      await contactSeller({
        variables: { listingContactId: listingId, message: formattedMessage },
      })

      trackEvent({
        action: GA_EVENT.RequestViewingSuccess,
        label: `ad_id=${listingId}`,
        customParameters: { eventCategory: source },
      })
      onSuccess()
    } catch (error) {
      trackEvent({
        action: GA_EVENT.RequestViewingFail,
        label: `ad_id=${listingId}`,
        customParameters: { eventCategory: source },
      })
      onError()
    }
  }

  const handleSubmitClick = (values: FormValues) => {
    const label = generateRequestViewingAttemptTrackingLabel({
      fullName: values.fullName,
      availableDates: values.availableDates,
      listingId,
    })

    trackEvent({
      action: GA_EVENT.RequestViewingAttempt,
      label,
      customParameters: {
        eventCategory: source,
      },
    })
  }

  const handleAddAvailableDate = useCallback(
    (push: (availableDate: AvailableDate) => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      push({
        date: '',
        timeFrom: '',
        timeTo: '',
      })
    },
    []
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validateDuplicateDates}>
      {({ values, errors, touched }) => (
        <Form>
          <FormBodyFlexContainer flexDirection="column" flexGrow={1}>
            <Field name="fullName" validate={validationFnPerField.name}>
              {({ field }: FieldProps) => (
                <TextField
                  label={t('common:forms.inputs.name.label')}
                  id="fullName"
                  error={
                    errors.fullName && touched.fullName ? t(String(errors.fullName)) : undefined
                  }
                  {...field}
                />
              )}
            </Field>

            <Spacing mTop={spacing.mini}>
              <BodyText size="large" color={colors.grey.primary} weight="medium">
                {t('request_viewing:form.availableDates.title')}
              </BodyText>
              <Spacing mTop={spacing.defaultSmall}>
                <BodyText size="medium" color={colors.grey.light1}>
                  {t('request_viewing:form.availableDates.subtitle')}
                </BodyText>
              </Spacing>
            </Spacing>

            <FieldArray name="availableDates">
              {({ remove, push }) => (
                <>
                  {values.availableDates?.map((_, index) => (
                    <AvailableDatesField
                      key={`${index}-availableDate`}
                      index={index}
                      remove={remove}
                      errors={errors}
                      values={values}
                      touched={touched}
                    />
                  ))}
                  {values.availableDates.length < MAX_AVAILABLE_DATES ? (
                    <Button variant="secondary" isFullWidth onClick={handleAddAvailableDate(push)}>
                      {t('request_viewing:form.availableDates.add_button')}
                    </Button>
                  ) : null}
                </>
              )}
            </FieldArray>

            <Spacing mTop={spacing.defaultSmall}>
              <BodyText color={colors.grey.light1}>{t('request_viewing:form.info')}</BodyText>
            </Spacing>
          </FormBodyFlexContainer>
          <FormFooter>
            <Button
              onClick={() => handleSubmitClick(values)}
              isLoading={isSubmittingMessage}
              type="submit"
              variant="primary"
              isFullWidth
            >
              {t('request_viewing:form.submit_button')}
            </Button>
          </FormFooter>
        </Form>
      )}
    </Formik>
  )
}
